import { Box, Container, Typography, Button, IconButton } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled } from '@mui/material/styles';
import { useEffect } from 'react';

const AnimatedButton = motion(Button);

const ScrollArrow = styled(motion(IconButton))(() => ({
  position: 'absolute',
  bottom: '16px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1,
  color: 'white',
  '& svg': {
    fontSize: '2.5rem',
  },
}));

const buttonVariants = {
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
      ease: 'easeInOut'
    }
  },
  tap: {
    scale: 0.95
  }
};

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (custom: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: custom * 0.2,
      duration: 0.8,
      ease: 'easeOut'
    }
  })
};

interface HeroProps {
  disableTopMargin?: boolean;
}

const Hero = ({ disableTopMargin = false }: HeroProps) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  return (
    <Box
      sx={{
        minHeight: disableTopMargin ? 'auto' : { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        p: { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
        mt: disableTopMargin ? 0 : { xs: '56px', sm: '64px' }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', pb: { xs: 8, sm: 10 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            custom={0}
            initial="hidden"
            animate={controls}
            variants={textVariants}
          >
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '2.5rem', sm: '3rem', md: '4.5rem' }, 
                fontWeight: 'bold', 
                mb: { xs: 1.5, sm: 2 }, 
                textAlign: { xs: 'center', md: 'left' },
                lineHeight: { xs: 1.2, sm: 1.3 }
              }}
            >
              MUMMY & DADDY COINS
            </Typography>
          </motion.div>
          <motion.div
            custom={1}
            initial="hidden"
            animate={controls}
            variants={textVariants}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, 
                mb: { xs: 1.5, sm: 2 }, 
                color: '#e0e0e0', 
                textAlign: { xs: 'center', md: 'left' },
                lineHeight: { xs: 1.3, sm: 1.4 }
              }}
            >
              Trade with Laughs, HODL with Love
            </Typography>
          </motion.div>
          <motion.div
            custom={2}
            initial="hidden"
            animate={controls}
            variants={textVariants}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }, 
                mb: { xs: 2.5, sm: 3, md: 4 }, 
                color: '#e0e0e0', 
                maxWidth: '800px', 
                lineHeight: { xs: 1.5, sm: 1.6 },
                px: { xs: 1, sm: 2, md: 0 }
              }}
            >
              Welcome to the wild, wacky world of Mummy and Daddy Coins—born from the chaotic heart of the trading floor, where fortunes rise, fall, and sometimes vanish faster than you can say "margin call!" These coins are a playful nod to the rollercoaster emotions of day trading, crafted to bring a chuckle to the serious world of markets. So, buckle up, lighten up, and let's dive into the spirit of Mummy and Daddy Coins!
            </Typography>
          </motion.div>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 2, sm: 3 }, 
            flexWrap: 'wrap', 
            justifyContent: { xs: 'center', md: 'flex-start' },
            width: '100%',
            mt: { xs: 1, sm: 2 },
            '& .MuiButton-root': {
              minWidth: { xs: '160px', sm: '180px' },
              flex: { xs: '1 1 auto', sm: '0 1 auto' },
              maxWidth: { xs: '200px', sm: 'none' }
            }
          }}>
            <AnimatedButton
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#ff4081',
                '&:hover': { backgroundColor: '#f50057' },
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
                py: { xs: 1.25, sm: 1.5 },
                px: { xs: 3, sm: 4 },
              }}
            >
              Get MUMMY Coin
            </AnimatedButton>
            <AnimatedButton
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              variant="outlined"
              size="large"
              onClick={() => {
                const aboutSection = document.getElementById('about-section');
                if (aboutSection) {
                  aboutSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': { borderColor: '#ff4081', color: '#ff4081' },
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
                py: { xs: 1.25, sm: 1.5 },
                px: { xs: 3, sm: 4 },
                cursor: 'pointer',
              }}
            >
              Learn More
            </AnimatedButton>
          </Box>
        </motion.div>
        <ScrollArrow
          aria-label="Scroll down"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <KeyboardArrowDownIcon />
        </ScrollArrow>
      </Container>
    </Box>
  );
};

export default Hero;
